import { useCallback, useMemo, useState } from "react"
import { propertyApi } from "@/api/client/property"
import type {
  PropertyPublicList200PropertiesItem,
  PropertyPublicList200PropertiesItemRoomsItem,
} from "@/api/generated/models"
import type { ComboboxOption } from "@/components/ui/combobox"

interface UsePropertySearchResult {
  // 物件検索
  propertyOptions: ComboboxOption[]
  isLoadingProperties: boolean
  hasMoreProperties: boolean
  handlePropertySearchChange: (value: string) => void
  handlePropertyLoadMore: () => void
  handlePropertyOpen: () => void
  // 選択された物件
  selectedProperty: PropertyPublicList200PropertiesItem | null
  selectProperty: (propertyId: string) => void
  // 部屋選択
  roomOptions: ComboboxOption[]
  selectedRoom: PropertyPublicList200PropertiesItemRoomsItem | null
  selectRoom: (roomId: string) => void
  // リセット
  reset: () => void
}

const formatPropertyAddress = (property: PropertyPublicList200PropertiesItem): string =>
  `${property.prefecture}${property.city}${property.town}${property.district}`

export function usePropertySearch(): UsePropertySearchResult {
  const [searchValue, setSearchValue] = useState("")
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null)
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null)

  // 物件一覧を取得（検索値に基づく）
  const { data: propertyData, isLoading: isLoadingProperties } = propertyApi.usePropertyPublicList({
    propertyName: searchValue || undefined,
  })

  // 物件オプションを生成
  const propertyOptions = useMemo<ComboboxOption[]>(() => {
    if (!propertyData?.properties) return []
    return propertyData.properties.map((property) => ({
      value: String(property.id),
      label: property.name,
      description: formatPropertyAddress(property),
    }))
  }, [propertyData?.properties])

  // 選択された物件情報
  const selectedProperty = useMemo(() => {
    if (!selectedPropertyId || !propertyData?.properties) return null
    return propertyData.properties.find((property) => property.id === selectedPropertyId) ?? null
  }, [selectedPropertyId, propertyData?.properties])

  // 部屋オプションを生成
  const roomOptions = useMemo<ComboboxOption[]>(() => {
    if (!selectedProperty?.rooms) return []
    return selectedProperty.rooms.map((room) => ({
      value: String(room.id),
      label: room.name,
    }))
  }, [selectedProperty?.rooms])

  // 選択された部屋情報
  const selectedRoom = useMemo(() => {
    if (!selectedRoomId || !selectedProperty?.rooms) return null
    return selectedProperty.rooms.find((room) => room.id === selectedRoomId) ?? null
  }, [selectedRoomId, selectedProperty?.rooms])

  // 物件検索値変更
  const handlePropertySearchChange = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  // 追加読み込み（public-list APIはページネーションなし）
  const handlePropertyLoadMore = useCallback(() => {
    // public-list APIはページネーションをサポートしていないため、何もしない
  }, [])

  // ポップオーバーを開いた時（検索値リセット）
  const handlePropertyOpen = useCallback(() => {
    setSearchValue("")
  }, [])

  // 物件選択
  const selectProperty = useCallback((propertyId: string) => {
    setSelectedPropertyId(propertyId ? Number(propertyId) : null)
    setSelectedRoomId(null) // 部屋選択をリセット
  }, [])

  // 部屋選択
  const selectRoom = useCallback((roomId: string) => {
    setSelectedRoomId(roomId ? Number(roomId) : null)
  }, [])

  // リセット
  const reset = useCallback(() => {
    setSearchValue("")
    setSelectedPropertyId(null)
    setSelectedRoomId(null)
  }, [])

  return {
    propertyOptions,
    isLoadingProperties,
    hasMoreProperties: false, // public-list APIはページネーションなし
    handlePropertySearchChange,
    handlePropertyLoadMore,
    handlePropertyOpen,
    selectedProperty,
    selectProperty,
    roomOptions,
    selectedRoom,
    selectRoom,
    reset,
  }
}
